import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Chart, ChartConfiguration, ChartType, registerables } from 'chart.js';

Chart.register(...registerables);
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Calendar, ChartLine, Database, LucideAngularModule, Settings2 } from 'lucide-angular';
import { DropdownComponent } from '@shared/components/input/dropdown/dropdown.component';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { Quadrant } from '@app/api/dmc';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  timeSeriesGraphVisualizationsMenuAction
} from '@shared/components/timeseries-graph/timeseries-graph-visualizations.menu-action';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';
import { TimeseriesGraphConfig } from '@shared/components/timeseries-graph/timeseries-graph-config';
import { TimeSeriesData } from '@shared/components/timeseries-graph/timeseries-data.interface';
import { defaultTimeseriesGraphConfig } from '@shared/components/timeseries-graph/default-timeseries-graph.config';
import { DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { DATE_FORMAT } from '@app/core/constants/date-format';
import { AlertService } from '@app/core/services/alert.service';
import { LoadingOverlayComponent } from '@shared/components/loading-overlay/loading-overlay.component';
import { GraphData } from '@shared/interfaces/timeseries-graph/graph-data.interface';
import { TimeSeriesGraphElement } from '@shared/interfaces/timeseries-graph/timeseries-graph-element.interface';
import { TimeSeriesDetails } from '@shared/interfaces/timeseries-graph/timeseries-details.interface';
import { median } from '@app/core/utils/median';

@Component({
  selector: 'app-timeseries-graph',
  imports: [
    FormsModule,
    LucideAngularModule,
    DropdownComponent,
    TranslatePipe,
    InputGroupComponent,
    LabelDirective,
    InputDateDirective,
    ReactiveFormsModule,
    ButtonDirective,
    ToggleComponent,
    LoadingOverlayComponent,
    NgClass,
    DecimalPipe
  ],
  templateUrl: './timeseries-graph.component.html',
  styleUrl: './timeseries-graph.component.css',
  providers: [DatePipe],
})
export class TimeseriesGraphComponent implements AfterViewInit, OnInit, OnDestroy, OnChanges {
  @ViewChild('chartCanvas', { static: false }) public chartCanvas!: ElementRef<HTMLCanvasElement>;

  /** Inputs **/
  @Input() public graphData: { datasets: TimeSeriesGraphElement[], labels: string[] } = { datasets: [], labels: [] };
  @Input() public config?: TimeseriesGraphConfig = defaultTimeseriesGraphConfig;

  /** Chart Type **/
  public selectedType: ChartType = 'line';

  /** Host Listeners **/
  @HostListener('window:resize', ['$event']) public windowResize(event: Event): void {
    this.chart?.resize();
  }

  /** Forms **/
  public dateForm: FormGroup = new FormGroup({
    start: new FormControl(),
    end: new FormControl(),
  })

  /** Icon **/
  protected readonly ChartLine = ChartLine;
  protected readonly Database = Database;
  protected readonly Calendar = Calendar;
  protected readonly Settings2 = Settings2;

  /** Data Sources **/
  public dataSources: MenuAction[] = []

  /** Active DataSources **/
  public dataSource: string[] = []

  /** Visualizations **/
  public visualizations: MenuAction[] = timeSeriesGraphVisualizationsMenuAction;

  /** Data Details **/
  public details?: TimeSeriesDetails[] = [];
  public showDetails: boolean = false;

  /** Chart Element **/
  public chart?: Chart;

  /** Legend Display **/
  public showLegend: boolean = false;

  /** Loading State **/
  public loading: boolean = false;

  // ─── Zoom state ────────────────────────────────────────────────────────────
  // Zoom state is tracked here explicitly — never read back from xScale.min/max,
  // which are internal Chart.js computed values and not reliably in sync with
  // options after an update('none') call.

  /** Whether zoom constraints are currently active on the x-axis. */
  private isZoomed = false;
  /** First visible data index when zoomed (integer). */
  private zoomMin = 0;
  /** Last visible data index when zoomed (integer). */
  private zoomMax = 0;
  /** Total label count — updated every drawGraph() so zoom math is always correct. */
  private totalDataPoints = 0;

  // ─── Selection-box state ───────────────────────────────────────────────────

  /** True while the user is holding the left mouse button and dragging. */
  private dragActive = false;
  /** Canvas-relative X/Y where the drag started (set on mousedown). */
  private dragStartCanvasX = 0;
  /** Canvas-relative X/Y of the latest tracked mouse position (updated on mousemove). */
  private dragCurrentCanvasX = 0;
  private dragCurrentCanvasY = 0;

  /** Whether the selection rectangle is currently visible. Bound in template. */
  public selectionActive = false;
  /** CSS-pixel geometry of the selection rectangle, relative to the canvas wrapper. */
  public selBox: { left: number; top: number; width: number; height: number } =
    { left: 0, top: 0, width: 0, height: 0 };

  // ─── Wheel listener ────────────────────────────────────────────────────────

  /**
   * Wheel handler attached with { passive: false } so event.preventDefault()
   * can suppress page scroll while the cursor is over the chart.
   *
   * Uses Chart.js's getValueForPixel() for pixel→index mapping instead of
   * manual arithmetic — this is always accurate regardless of whether a zoom
   * range is currently active.
   */
  private readonly onWheelHandler = (e: WheelEvent): void => {
    if (!this.chart || this.totalDataPoints < 2) return;
    e.preventDefault();

    const xScale = this.chart.scales['x'];
    if (!xScale || xScale.width === 0) return;

    const currentMin = this.isZoomed ? this.zoomMin : 0;
    const currentMax = this.isZoomed ? this.zoomMax : this.totalDataPoints - 1;
    const currentRange = currentMax - currentMin;
    if (currentRange < 1) return;

    // Data index under the cursor — keeps that point visually fixed during zoom.
    const cursorIdx = xScale.getValueForPixel(e.offsetX) ?? (currentMin + currentRange / 2);

    // 25% range change per scroll notch. deltaY > 0 = scroll down = zoom out.
    const factor = e.deltaY > 0 ? 1.25 : 0.8;
    const newRange = currentRange * factor;

    const fraction = (cursorIdx - currentMin) / currentRange;

    // Round outward when zooming out (expand the range by at least 1 integer unit),
    // and inward when zooming in (shrink it). Using Math.round for both produces
    // identical integers at small ranges — the zoom then has no visible effect.
    const zoomingOut = e.deltaY > 0;
    let newMin = zoomingOut
      ? Math.floor(cursorIdx - fraction * newRange)
      : Math.ceil(cursorIdx - fraction * newRange);
    let newMax = zoomingOut
      ? Math.ceil(cursorIdx + (1 - fraction) * newRange)
      : Math.floor(cursorIdx + (1 - fraction) * newRange);

    newMin = Math.max(0, newMin);
    newMax = Math.min(this.totalDataPoints - 1, newMax);
    if (newMax - newMin < 1) return;

    // If we've zoomed back to the full dataset, lift constraints entirely.
    if (newMin === 0 && newMax === this.totalDataPoints - 1) {
      this.isZoomed = false;
    } else {
      this.isZoomed = true;
      this.zoomMin = newMin;
      this.zoomMax = newMax;
    }

    this.applyZoom();
  };

  constructor(
    private readonly translateService: TranslateService,
    private readonly datePipe: DatePipe,
    private readonly alertService: AlertService,
  ) {
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Wheel listener is attached once here rather than in drawGraph() because
   * the canvas DOM element is stable for the entire component lifetime —
   * Chart.js reuses the same <canvas> element across destroy/recreate cycles.
   * Attaching it here eliminates any per-render attach/detach timing issues.
   */
  public ngAfterViewInit(): void {
    this.chartCanvas.nativeElement.addEventListener('wheel', this.onWheelHandler, { passive: false });
  }

  public ngOnInit(): void {
    this.initialize();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedType'] && !changes['selectedType'].firstChange && this.chart) {
      this.chart.destroy();
      this.drawGraph();
    }

    if (changes['graphData'] && !changes['graphData'].firstChange && this.chart) {
      this.totalDataPoints = this.graphData.labels.length;
      this.chart.data.labels = this.graphData.labels;
      this.chart.data.datasets = this.graphData.datasets;
      this.chart.update();
    }
  }

  public ngOnDestroy(): void {
    this.chartCanvas.nativeElement.removeEventListener('wheel', this.onWheelHandler);
    this.chart?.destroy();
  }

  // ─── Mouse interaction (selection-box zoom) ────────────────────────────────

  /**
   * Bound to (mousedown) on the canvas in the template.
   * Starts a selection drag; preventDefault() suppresses browser text-selection
   * and native drag-and-drop behaviour that would steal subsequent mousemove events.
   */
  public onMouseDown(e: MouseEvent): void {
    if (e.button !== 0) return;
    e.preventDefault();

    this.dragActive = true;
    this.selectionActive = false;
    this.dragStartCanvasX = e.offsetX;
    this.dragCurrentCanvasX = e.offsetX;
    this.dragCurrentCanvasY = e.offsetY;
  }

  /**
   * Document-level listener so the selection rectangle continues to track the
   * cursor even when it leaves the canvas boundary mid-drag.
   * Uses getBoundingClientRect() to convert client coordinates to canvas-relative
   * coordinates, matching the coordinate space of offsetX/offsetY.
   */
  @HostListener('document:mousemove', ['$event'])
  public onDocumentMouseMove(e: MouseEvent): void {
    if (!this.dragActive || !this.chart) return;

    const rect = this.chartCanvas.nativeElement.getBoundingClientRect();
    this.dragCurrentCanvasX = e.clientX - rect.left;
    this.dragCurrentCanvasY = e.clientY - rect.top;

    const xScale = this.chart.scales['x'];
    if (!xScale) return;

    // Only show the selection rectangle once the drag exceeds 5 px to avoid
    // triggering on plain clicks.
    if (Math.abs(this.dragCurrentCanvasX - this.dragStartCanvasX) < 5) return;

    this.selectionActive = true;

    // Clamp both edges to the visible chart area.
    const clamp = (x: number) => Math.max(xScale.left, Math.min(xScale.right, x));
    const startX = clamp(this.dragStartCanvasX);
    const endX   = clamp(this.dragCurrentCanvasX);

    this.selBox = {
      left:   Math.min(startX, endX),
      top:    xScale.top,
      width:  Math.abs(endX - startX),
      height: xScale.bottom - xScale.top,
    };
  }

  /**
   * Document-level listener so zoom is committed even when the mouse button is
   * released outside the canvas.
   */
  @HostListener('document:mouseup')
  public onDocumentMouseUp(): void {
    if (!this.dragActive) return;

    this.dragActive = false;

    if (this.selectionActive && this.chart) {
      this.applySelectionZoom();
    }

    this.selectionActive = false;
  }

  // ─── Zoom helpers ──────────────────────────────────────────────────────────

  /**
   * Converts the selection rectangle pixel coordinates to data indices using
   * Chart.js's getValueForPixel() and applies them as the new zoom range.
   */
  private applySelectionZoom(): void {
    if (!this.chart) return;
    const xScale = this.chart.scales['x'];
    if (!xScale) return;

    const startX = Math.min(this.dragStartCanvasX, this.dragCurrentCanvasX);
    const endX   = Math.max(this.dragStartCanvasX, this.dragCurrentCanvasX);

    const rawMin = xScale.getValueForPixel(startX);
    const rawMax = xScale.getValueForPixel(endX);
    if (rawMin == null || rawMax == null) return;

    const newMin = Math.max(0, Math.round(rawMin));
    const newMax = Math.min(this.totalDataPoints - 1, Math.round(rawMax));
    if (newMax <= newMin) return;

    this.zoomMin  = newMin;
    this.zoomMax  = newMax;
    this.isZoomed = true;
    this.applyZoom();
  }

  /**
   * Pushes the zoom state into chart options and triggers a no-animation update.
   * When not zoomed, min/max are deleted entirely so Chart.js auto-scales.
   */
  private applyZoom(): void {
    if (!this.chart) return;
    const xOpts = this.chart.options.scales!['x']!;
    if (this.isZoomed) {
      xOpts.min = this.zoomMin;
      xOpts.max = this.zoomMax;
    } else {
      delete (xOpts as any).min;
      delete (xOpts as any).max;
    }
    this.chart.update('none');
  }

  // ─── Chart rendering ───────────────────────────────────────────────────────

  private initialize(): void {
    if (this.config) {
      this.config = { ...defaultTimeseriesGraphConfig, ...this.config };

      if (this.config.dateRange) {
        if (this.config.startDate && this.config.endDate) {
          this.dateForm.patchValue({ start: this.config.startDate, end: this.config.endDate });
        } else {
          this.dateForm.patchValue({
            start: new Date(Date.now() - 3600000 * 24 * 30),
            end: new Date(),
          });
        }
      }

      if (this.config.showLegend) this.showLegend = this.config.showLegend;
      if (this.config.defaultVisualization) this.selectedType = this.config.defaultVisualization;

      if (this.config?.availableVisualizations && this.config.availableVisualizations.length > 0) {
        this.visualizations = timeSeriesGraphVisualizationsMenuAction.filter(v =>
          this.config!.availableVisualizations!.includes(v.id as any)
        );
      }

      if (this.config.callback) this.fetchData();
    }

    if (!this.config?.callback) {
      setTimeout(() => this.drawGraph());
    }
  }

  public reloadChart(): void {
    this.chart?.destroy();
    this.drawGraph();
  }

  public drawGraph(): void {
    const labels: string[] = this.graphData.labels;
    this.totalDataPoints = labels.length;

    const totalAnimationMs = 300;
    const animationDelayStepMs = totalAnimationMs / Math.max(labels.length, 1);

    let datasets: TimeSeriesGraphElement[] = this.graphData.datasets;
    if (this.dataSource.length > 0) {
      datasets = this.graphData.datasets.filter(d => this.dataSource.includes(d.id));
    }

    const config: ChartConfiguration = {
      type: this.selectedType,
      data: {
        labels: this.graphData.labels,
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          delay: (context) => {
            if (context.type !== 'data' || context.mode !== 'default') return 0;
            return context.dataIndex * animationDelayStepMs;
          },
        },
        plugins: {
          legend: { display: this.showLegend },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: !!this.config?.yAxisLabel,
              text: this.config?.yAxisLabel || '',
              font: { size: 12 },
            },
          },
          x: {
            ticks: { autoSkip: true },
          },
        },
      },
    };

    this.chart = new Chart(this.chartCanvas.nativeElement, config);

    // Re-apply zoom after a chart redraw (e.g. chart-type toggle).
    // Validates that the stored range is still within bounds for the new dataset.
    if (this.isZoomed) {
      this.zoomMax = Math.min(this.zoomMax, this.totalDataPoints - 1);
      if (this.zoomMax > this.zoomMin) {
        this.applyZoom();
      } else {
        this.isZoomed = false;
      }
    }
  }

  public fetchData(): void {
    const cb = this.config?.callback;
    if (typeof cb !== 'function') return;
    this.loading = true;

    const request = this.config?.dateRange ? cb(this.dateForm.value) : cb();

    request
      .then((data: TimeSeriesData[]) => {
        // Reset zoom whenever new data arrives so the user sees the full dataset.
        this.isZoomed = false;

        this.graphData = this.repackData(data, this.config?.dateFormat);
        if (this.chart) this.chart.destroy();
        this.drawGraph();
      })
      .catch((err: any) => {
        this.alertService.danger('header', err);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  // ─── Data helpers ──────────────────────────────────────────────────────────

  private getDetailsStructure(graphData: GraphData): TimeSeriesDetails[] {
    this.details = [];
    const detailElements: TimeSeriesDetails[] = [];

    for (const dataset of graphData.datasets) {
      const total = dataset.data.reduce((sum: number, value: number) => sum + value, 0);
      detailElements.push({
        id: this.translateService.instant(`common.graph.${dataset.id.toLowerCase()}`),
        min: Math.min(...dataset.data),
        max: Math.max(...dataset.data),
        average: total / dataset.data.length,
        median: median([...dataset.data]),
      });
    }

    return detailElements;
  }

  private restructureForPie(graphData: GraphData): GraphData {
    const labels: string[] = [];
    const data: number[] = [];
    const backgroundColor: string[] = [];
    const colors = ['#007bff', '#dc3545', '#ffc107', '#28a745', '#17a2b8', '#6610f2', '#e83e8c', '#fd7e14'];

    graphData.datasets.forEach((dataset, index) => {
      const total = dataset.data.reduce((sum, value) => sum + value, 0);
      labels.push(dataset.label);
      data.push(total);
      backgroundColor.push(dataset.color || colors[index % colors.length]);
    });

    return {
      labels,
      datasets: [{
        id: 'pie-aggregate',
        label: 'Total Consumption',
        data,
        color: backgroundColor.join(','),
      }],
    };
  }

  private repackData(timeSeriesData: TimeSeriesData[], dateFormat: string = DATE_FORMAT): GraphData {
    const seriesMap = new Map<string, Map<string, number>>();
    const labelSet = new Set<string>();

    for (const item of timeSeriesData) {
      const id = item.id;
      const formattedTs =
        this.datePipe.transform(new Date(item.data.timestamp), dateFormat) ?? item.data.timestamp;

      labelSet.add(formattedTs);

      let idMap = seriesMap.get(id);
      if (!idMap) {
        idMap = new Map<string, number>();
        seriesMap.set(id, idMap);
      }
      idMap.set(formattedTs, item.data.value);
    }

    const labels = Array.from(labelSet).sort((a, b) => a.localeCompare(b));

    if (this.dataSources) this.dataSources.length = 0;

    const datasets: TimeSeriesGraphElement[] = [];
    for (const [id, idMap] of seriesMap) {
      const label = this.translateService.instant(`common.graph.${id.toLowerCase()}`);
      if (this.dataSources) this.dataSources.push({ id, title: label });
      datasets.push({ id, label, data: labels.map(ts => idMap.get(ts) ?? 0) });
    }

    if (this.dataSource.length === 0) {
      for (const d of this.dataSources) this.dataSource.push(d.id);
    }

    this.details = this.getDetailsStructure({ datasets, labels });
    return { datasets, labels };
  }

  // ─── UI callbacks ──────────────────────────────────────────────────────────

  public dataSourceChanged(event: string | string[]): void {
    if (typeof event === 'string' || !this.chart) return;

    this.chart.data.datasets = this.graphData.datasets
      .filter(d => event.includes(d.id))
      .map(dataset => ({ ...dataset }));

    this.chart.update();
  }

  public visualizationChanged(event: string | string[]): void {
    if (typeof event === 'string') this.reloadChart();
  }

  public toggleLegend(event: boolean): void {
    this.showLegend = event;
    if (this.chart) {
      this.chart.options.plugins!.legend!.display = this.showLegend;
      this.chart.update();
    }
  }

  public reset(): void {
    this.isZoomed = false;
    this.dataSource = [Quadrant.Consumption, Quadrant.Production];
    this.reloadChart();
  }
}
